import { useEffect, useRef } from 'react';

import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  Polyline,
  useMap,
  useMapEvents
} from 'react-leaflet';

import MarkerClusterGroup from 'react-leaflet-cluster';

import L from 'leaflet';
import { chileTerritory } from '../data/chileTerritory';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.heat';

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      if (onMapClick) {
        onMapClick(e.latlng.lat.toFixed(6), e.latlng.lng.toFixed(6));
      }
    }
  });

  return null;
}

function FitBounds({ points, selectedEntity }) {
  const map = useMap();

  const pointsKey = points
    .map((p) => `${p[0]},${p[1]}`)
    .sort()
    .join('|');

  useEffect(() => {
    if (selectedEntity && tieneCoordenadas(selectedEntity)) return;
    if (!points || points.length === 0) return;

    const bounds = L.latLngBounds(points);

    map.fitBounds(bounds, {
      padding: [45, 45],
      maxZoom: 13
    });
  }, [pointsKey, map, selectedEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function FocusSelectedEntity({ selectedEntity }) {
  const map = useMap();

  useEffect(() => {
    if (!selectedEntity || !tieneCoordenadas(selectedEntity)) return;

    const lat = getLat(selectedEntity);
    const lng = getLng(selectedEntity);

    map.flyTo([lat, lng], Math.max(map.getZoom(), 14), {
      animate: true,
      duration: 0.8
    });
  }, [selectedEntity, map]);

  return null;
}

function FocusProjectRegion({ activeProject, selectedEntity, hasPoints }) {
  const map = useMap();

  useEffect(() => {
    if (selectedEntity && tieneCoordenadas(selectedEntity)) return;
    if (hasPoints) return;
    if (!activeProject || !activeProject.region) return;

    const region = chileTerritory.find(
      (r) => r.region === activeProject.region
    );

    if (!region || region.lat == null || region.lng == null) return;

    map.flyTo([region.lat, region.lng], 8, {
      animate: true,
      duration: 0.8
    });
  }, [activeProject, selectedEntity, hasPoints, map]);

  return null;
}

function ResizeMapOnContainerChange() {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    let container;

    try {
      container = map.getContainer();
    } catch (error) {
      return;
    }

    if (!container) return;

    let resizeTimeout;

    const safeInvalidate = () => {
      clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        try {
          const currentContainer = map.getContainer();

          if (currentContainer && currentContainer._leaflet_pos !== undefined) {
            map.invalidateSize(false);
          }
        } catch (error) {
          // Evita error cuando Leaflet ya desmontó el mapa
        }
      }, 180);
    };

    const resizeObserver = new ResizeObserver(() => {
      safeInvalidate();
    });

    resizeObserver.observe(container);
    safeInvalidate();

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [map]);

  return null;
}

function TileFilterControl() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    container.classList.add('vita-osm-soft');

    const styleId = 'vita-osm-soft-style';

    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');

      style.id = styleId;

      style.innerHTML = `
        .vita-osm-soft .leaflet-tile {
          filter: saturate(0.78) contrast(1.12) brightness(0.97) !important;
        }

        .vita-cluster-icon {
          background: transparent;
          border: none;
        }

        .vita-cluster-bubble {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: linear-gradient(135deg,#0f766e,#0891b2);
          color: white;
          border: 3px solid rgba(255,255,255,0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          font-size: 14px;
          box-shadow:
            0 0 18px rgba(8,145,178,0.55),
            0 12px 24px rgba(0,0,0,0.25);
        }
      `;

      document.head.appendChild(style);
    }

    return () => {
      container.classList.remove('vita-osm-soft');
    };
  }, [map]);

  return null;
}

function SocialHeatLayer({ signals }) {
  const map = useMap();

  useEffect(() => {
    if (!signals || signals.length === 0) return;

    const heatPoints = signals
      .filter((signal) => tieneCoordenadas(signal))
      .map((signal) => {
        const lat = getLat(signal);
        const lng = getLng(signal);

        let intensity = 0.72;

        if (
          signal.sentimiento === 'Negativo' ||
          signal.riesgo === 'Alto' ||
          signal.riesgo === 'Crítico'
        ) {
          intensity = 1;
        }

        if (signal.sentimiento === 'Positivo') {
          intensity = 0.82;
        }

        return [lat, lng, intensity];
      });

    if (heatPoints.length === 0) return;

    const heatLayer = L.heatLayer(heatPoints, {
      radius: 40,
      blur: 24,
      maxZoom: 14,
      minOpacity: 0.42,
      gradient: {
        0.2: '#06b6d4',
        0.45: '#22d3ee',
        0.65: '#facc15',
        0.82: '#f97316',
        1.0: '#dc2626'
      }
    });

    heatLayer.addTo(map);

    return () => {
      map.removeLayer(heatLayer);
    };
  }, [signals, map]);

  return null;
}


function getLat(item) {
  return Number(
    item.latitud ||
      item.lat ||
      item.latitude ||
      item.Latitud ||
      item.Latitude
  );
}

function getLng(item) {
  return Number(
    item.longitud ||
      item.lng ||
      item.lon ||
      item.longitude ||
      item.Longitud ||
      item.Longitude
  );
}

function coordenadaValida(lat, lng) {
  return lat > -56 && lat < -17 && lng > -76 && lng < -66;
}

function tieneCoordenadas(item) {
  const lat = getLat(item);
  const lng = getLng(item);

  return !Number.isNaN(lat) && !Number.isNaN(lng) && coordenadaValida(lat, lng);
}

function coordenada(item) {
  return [getLat(item), getLng(item)];
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function sameText(a, b) {
  return normalize(a) && normalize(a) === normalize(b);
}

function includesText(text, value) {
  const cleanText = normalize(text);
  const cleanValue = normalize(value);

  if (!cleanText || !cleanValue) return false;

  return cleanText.includes(cleanValue);
}

function getTags(item) {
  return String(item.tags || '')
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
}

function shareTags(a, b) {
  const tagsA = getTags(a);
  const tagsB = getTags(b);

  if (tagsA.length === 0 || tagsB.length === 0) return false;

  return tagsA.some((tag) => tagsB.includes(tag));
}

function isSameEntity(item, selectedEntity) {
  if (!item || !selectedEntity) return false;

  if (item.id && selectedEntity.id && item.id === selectedEntity.id) {
    return true;
  }

  return (
    sameText(item.nombre, selectedEntity.nombre) ||
    sameText(item.titulo, selectedEntity.titulo) ||
    sameText(item.red, selectedEntity.red)
  );
}

function isRelatedToSelected(item, selectedEntity) {
  if (!selectedEntity) return false;
  if (isSameEntity(item, selectedEntity)) return false;

  return (
    sameText(item.actor, selectedEntity.nombre) ||
    sameText(item.actor, selectedEntity.actor) ||
    sameText(item.nombre, selectedEntity.actor) ||
    sameText(item.foco, selectedEntity.foco) ||
    sameText(item.sector, selectedEntity.sector) ||
    sameText(item.comuna, selectedEntity.comuna) ||
    sameText(item.ubicacion, selectedEntity.comuna) ||
    includesText(item.descripcion, selectedEntity.nombre) ||
    includesText(item.texto, selectedEntity.nombre) ||
    includesText(item.tags, selectedEntity.nombre) ||
    shareTags(item, selectedEntity)
  );
}

function getRelationState(item, selectedEntity, showRelations) {
  if (!selectedEntity || !showRelations) return 'normal';
  if (isSameEntity(item, selectedEntity)) return 'selected';
  if (isRelatedToSelected(item, selectedEntity)) return 'related';

  return 'muted';
}

function buildConnectionLines({
  selectedEntity,
  actores
}) {
  if (!selectedEntity || !tieneCoordenadas(selectedEntity)) return [];

  const relacional = selectedEntity._vita?.relacional;
  if (!relacional || relacional.grado === 0) return [];

  const origin = coordenada(selectedEntity);

  return relacional.actoresConectados
    .map((conexion) => {
      const actorConectado = actores.find(a => a.id === conexion.actorId);
      if (!actorConectado || !tieneCoordenadas(actorConectado)) return null;

      const color =
        conexion.tipo === 'Convergencia' ? '#ef4444' :
        conexion.tipo === 'Aliado' ? '#22c55e' :
        conexion.tipo === 'Tensión' ? '#f97316' :
        '#67e8f9';

      return {
        id: `rel-${conexion.actorId}`,
        positions: [origin, coordenada(actorConectado)],
        color,
        tipo: conexion.tipo,
        intensidad: conexion.intensidad
      };
    })
    .filter(Boolean);
}

function crearIconoMapa({ texto, color, relationState = 'normal' }) {
  const isMuted = relationState === 'muted';
  const isRelated = relationState === 'related';
  const isSelected = relationState === 'selected';

  const size = isSelected ? 34 : isRelated ? 32 : 30;
  const innerSize = isSelected ? 25 : isRelated ? 23 : 21;
  const opacity = isMuted ? 0.3 : 1;
  const glowOpacity = isMuted ? 0.04 : isRelated || isSelected ? 0.28 : 0.13;

  return L.divIcon({
    className: '',
    html: `
      <div style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        display:flex;
        align-items:center;
        justify-content:center;
        opacity:${opacity};
      ">
        <div style="
          position:absolute;
          width:${size}px;
          height:${size}px;
          border-radius:50%;
          background:${color};
          opacity:${glowOpacity};
          filter:blur(6px);
        "></div>

        <div style="
          width:${innerSize}px;
          height:${innerSize}px;
          border-radius:50%;
          background:${color};
          border:2px solid rgba(255,255,255,0.9);
          box-shadow:
            0 0 ${isRelated || isSelected ? '18px' : '10px'} ${color},
            0 6px 14px rgba(0,0,0,0.20);
          display:flex;
          align-items:center;
          justify-content:center;
          color:white;
          font-weight:900;
          font-size:11px;
          z-index:2;
        ">
          ${texto}
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}

function crearIconoSeleccionado() {
  return L.divIcon({
    className: '',
    html: `
      <div style="
        position: relative;
        width: 54px;
        height: 54px;
        display:flex;
        align-items:center;
        justify-content:center;
      ">
        <div style="
          position:absolute;
          width:54px;
          height:54px;
          border-radius:50%;
          background:#67e8f9;
          opacity:0.18;
          filter:blur(8px);
        "></div>

        <div style="
          width:40px;
          height:40px;
          border-radius:50%;
          border:2px solid rgba(255,255,255,0.95);
          box-shadow:
            0 0 22px rgba(103,232,249,0.9),
            0 0 42px rgba(6,182,212,0.65);
          display:flex;
          align-items:center;
          justify-content:center;
          color:white;
          font-weight:900;
          font-size:18px;
          z-index:2;
          background:linear-gradient(135deg,#0891b2,#0f766e);
        ">
          ⦿
        </div>
      </div>
    `,
    iconSize: [54, 54],
    iconAnchor: [27, 27]
  });
}

function crearIconoInvisible() {
  return L.divIcon({
    className: '',
    html: '<div style="width:22px;height:22px;border-radius:50%;"></div>',
    iconSize: [22, 22],
    iconAnchor: [11, 11]
  });
}

function crearIconoCluster(cluster) {
  const count = cluster.getChildCount();

  return L.divIcon({
    html: `<div class="vita-cluster-bubble">${count}</div>`,
    className: 'vita-cluster-icon',
    iconSize: L.point(38, 38, true)
  });
}

function getActorColor(actor) {
  const riesgo = normalize(actor.riesgo);
  const estado = normalize(actor.estado);
  const influencia = normalize(actor.influencia);

  if (
    riesgo.includes('critico') ||
    riesgo.includes('crítico') ||
    estado.includes('critico') ||
    estado.includes('crítico')
  ) {
    return '#dc2626';
  }

  if (
    riesgo.includes('alto') ||
    influencia.includes('alta')
  ) {
    return '#f97316';
  }

  if (
    riesgo.includes('medio') ||
    estado.includes('observacion') ||
    estado.includes('observación')
  ) {
    return '#eab308';
  }

  if (
    estado.includes('positivo') ||
    riesgo.includes('bajo')
  ) {
    return '#16a34a';
  }

  return '#2563eb';
}
function getAlertColor(alerta) {
  if (alerta.nivel === 'Crítica') return '#dc2626';
  if (alerta.nivel === 'Alta') return '#d97706';
  return '#eab308';
}

function getSignalColor(signal) {
  if (
    signal.sentimiento === 'Negativo' ||
    signal.riesgo === 'Alto' ||
    signal.riesgo === 'Crítico'
  ) {
    return '#dc2626';
  }

  if (signal.sentimiento === 'Positivo') {
    return '#16a34a';
  }

  return '#06b6d4';
}

function TooltipBox({ title, subtitle, color, relationState }) {
  const label =
    relationState === 'selected'
      ? 'Entidad activa'
      : relationState === 'related'
        ? 'Relacionado'
        : relationState === 'muted'
          ? 'No relacionado'
          : '';

  return (
    <div style={tooltipStyles.box}>
      <strong style={tooltipStyles.title}>{title || '-'}</strong>

      <span style={{ ...tooltipStyles.subtitle, color }}>
        {subtitle || 'Registro territorial'}
      </span>

      {label && (
        <span style={tooltipStyles.relationLabel}>
          {label}
        </span>
      )}
    </div>
  );
}

function MapView({
  projectActors = [],
  projectAlerts = [],
  projectSignals = [],
  mode = 'view',
  selectedCoordinates,
  selectedEntity,
  showRelations = false,
  pdfMode = false,
  activeProject,
  onMapClick,
  onTerritorialSelect
}) {
  const actores = projectActors
    .filter(tieneCoordenadas)
    .map((actor) => ({
      ...actor,
      entityType: 'stakeholder'
    }));

  const alertas = projectAlerts
    .filter(tieneCoordenadas)
    .map((alerta) => ({
      ...alerta,
      entityType: 'alert'
    }));

  const signals = projectSignals
    .filter(tieneCoordenadas)
    .map((signal) => ({
      ...signal,
      entityType: 'signal'
    }));

  const selectedPoint =
    selectedCoordinates?.latitud && selectedCoordinates?.longitud
      ? [Number(selectedCoordinates.latitud), Number(selectedCoordinates.longitud)]
      : null;

  const selectedEntityPoint =
    selectedEntity && tieneCoordenadas(selectedEntity)
      ? coordenada(selectedEntity)
      : null;

  const allPoints = [
    ...actores.map(coordenada),
    ...alertas.map(coordenada),
    ...signals.map(coordenada),
    ...(selectedPoint ? [selectedPoint] : [])
  ];

  const connectionLines = showRelations
    ? buildConnectionLines({
        selectedEntity,
        actores
      })
    : [];

  const center = allPoints.length > 0 ? allPoints[0] : [-32.7808, -70.9639];

  return (
    <MapContainer
  center={center}
  zoom={11}
  zoomControl={!pdfMode}
  attributionControl={!pdfMode}
  dragging={!pdfMode}
  scrollWheelZoom={!pdfMode}
  doubleClickZoom={!pdfMode}
  boxZoom={!pdfMode}
  keyboard={!pdfMode}
  touchZoom={!pdfMode}
  style={{
    width: '100%',
    height: '100%',
    background: '#071a2f'
  }}
>
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <ResizeMapOnContainerChange />

      <TileFilterControl />

      <FitBounds points={allPoints} selectedEntity={selectedEntity} />

      <FocusSelectedEntity selectedEntity={selectedEntity} />

      <FocusProjectRegion
        activeProject={activeProject}
        selectedEntity={selectedEntity}
        hasPoints={allPoints.length > 0}
      />

      <SocialHeatLayer signals={signals} />

      {connectionLines.map((line) => (
        <Polyline
          key={line.id}
          positions={line.positions}
          pathOptions={{
            color: line.color,
            weight: line.intensidad === 'Alta' ? 3 : line.intensidad === 'Media' ? 2 : 1.5,
            opacity: 0.85,
            dashArray: line.tipo === 'Tensión' ? '6, 6' : null
          }}
        />
      ))}

      {mode === 'select' && <MapClickHandler onMapClick={onMapClick} />}

      <MarkerClusterGroup
  chunkedLoading
  maxClusterRadius={65}
  spiderfyOnMaxZoom={false}
  zoomToBoundsOnClick={false}
  showCoverageOnHover={false}
  spiderLegPolylineOptions={{
    weight: 2,
    color: '#67e8f9',
    opacity: 0.75
  }}
  iconCreateFunction={crearIconoCluster}
  eventHandlers={{
    clustermouseover: (event) => {
      event.layer.spiderfy();
    },

    clusterclick: (event) => {
      event.layer.spiderfy();
    }
  }}
>
      
        {actores.map((actor) => {
          const color = getActorColor(actor);
          const relationState = getRelationState(
            actor,
            selectedEntity,
            showRelations
          );

          return (
            <Marker
              key={`actor-${actor.id}`}
              position={coordenada(actor)}
              icon={crearIconoMapa({
                texto: 'S',
                color,
                relationState
              })}
              eventHandlers={{
                click: () => {
                  if (onTerritorialSelect) {
                    onTerritorialSelect({
                      ...actor,
                      entityType: 'stakeholder',
                      panelType: 'Stakeholder territorial'
                    });
                  }
                }
              }}
            >
              {!pdfMode && (
  <Tooltip
    direction="top"
    offset={[0, -10]}
    opacity={1}
    permanent={false}
    sticky
  >
    <TooltipBox
      title={actor.nombre}
      subtitle={`Riesgo: ${actor.riesgo || '-'} · Estado: ${actor.estado || '-'}`}
      color={color}
      relationState={relationState}
    />
  </Tooltip>
)}
            </Marker>
          );
        })}

        {alertas.map((alerta) => {
  const color = getAlertColor(alerta);
  const relationState = getRelationState(
    alerta,
    selectedEntity,
    showRelations
  );

  return (
    <Marker
      key={`alerta-${alerta.id}`}
      position={coordenada(alerta)}
      icon={crearIconoMapa({
        texto: '!',
        color,
        relationState
      })}
      eventHandlers={{
        click: () => {
          if (onTerritorialSelect) {
            onTerritorialSelect({
              ...alerta,
              entityType: 'alert',
              panelType: 'Alerta territorial'
            });
          }
        }
      }}
    >
      {!pdfMode && (
        <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false} sticky>
          <TooltipBox
            title={alerta.titulo}
            subtitle={`Nivel: ${alerta.nivel || '-'} · Estado: ${alerta.estado || '-'}`}
            color={color}
            relationState={relationState}
          />
        </Tooltip>
      )}
    </Marker>
  );
})}
</MarkerClusterGroup>

{signals.map((signal) => {
  const color = getSignalColor(signal);
  const position = coordenada(signal);
  const relationState = getRelationState(
    signal,
    selectedEntity,
    showRelations
  );

  return (
    <Marker
      key={`signal-tooltip-${signal.id}`}
      position={position}
      icon={crearIconoInvisible()}
      eventHandlers={{
        click: () => {
          if (onTerritorialSelect) {
            onTerritorialSelect({
              ...signal,
              entityType: 'signal',
              panelType: 'Pulso social territorial'
            });
          }
        }
      }}
    >
      {!pdfMode && (
        <Tooltip direction="top" offset={[0, -6]} opacity={1} permanent={false} sticky>
          <TooltipBox
            title={signal.red || 'Pulso social'}
            subtitle={`Sentimiento: ${signal.sentimiento || '-'} · Riesgo: ${signal.riesgo || '-'}`}
            color={color}
            relationState={relationState}
          />
        </Tooltip>
      )}
    </Marker>
  );
})}

{selectedEntityPoint && (
  <Marker position={selectedEntityPoint} icon={crearIconoSeleccionado()}>
    {!pdfMode && (
      <Tooltip direction="top" offset={[0, -18]} opacity={1}>
        <TooltipBox
          title={
            selectedEntity.nombre ||
            selectedEntity.titulo ||
            selectedEntity.red ||
            'Entidad activa'
          }
          subtitle="Entidad activa"
          color="#0891b2"
          relationState="selected"
        />
      </Tooltip>
    )}
  </Marker>
)}

{selectedPoint && coordenadaValida(selectedPoint[0], selectedPoint[1]) && (
  <Marker
    position={selectedPoint}
    icon={crearIconoMapa({
      texto: '✓',
      color: '#0993b5'
    })}
  >
    {!pdfMode && (
      <Tooltip direction="top" offset={[0, -10]} opacity={1} sticky>
        <TooltipBox
          title="Coordenada seleccionada"
          subtitle={`${selectedPoint[0]}, ${selectedPoint[1]}`}
          color="#0993b5"
        />
      </Tooltip>
    )}
  </Marker>
)}
</MapContainer>
  );
}

const tooltipStyles = {
  box: {
    fontFamily: 'Arial',
    minWidth: '160px'
  },

  title: {
    display: 'block',
    color: '#071a2f',
    fontSize: '13px',
    marginBottom: '3px'
  },

  subtitle: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '800'
  },

  relationLabel: {
    display: 'inline-block',
    marginTop: '5px',
    padding: '3px 7px',
    borderRadius: '999px',
    background: '#ecfeff',
    color: '#0e7490',
    fontSize: '10px',
    fontWeight: '900'
  }
};

export default MapView;